import React, { useState, useEffect } from "react";
import styles from "./HistoryBody.module.css";
import { API_URL } from "../../config/api";

const HistoryBody = ({ warehouse }) => {
  const [activeTab, setActiveTab] = useState("sales");
  const [sales, setSales] = useState([]);
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);

  const statusMap = {
    paid: "Оплачен",
    partially_paid_fiscalized: "Частично оплачен (фискализирован)",
    partially_paid: "Частично оплачен",
    fiscalized: "Фискализирован",
    canceled: "Отменён",
    returned: "Возврат",
    pending: "Ожидает",
    paid_and_fiscalized: "Оплачен (фискализирован)",
    paid_but_fiscalization_required: "Оплачен (требуется фискализация)",
  };

  const translateStatus = (status) => {
    if (!status) return "—";
    return statusMap[status] || status; // если неизвестный — покажем как есть
  };

  useEffect(() => {
    if (!warehouse?.id) return;

    const token = localStorage.getItem("token");
    if (!token) {
      console.error("❌ Нет токена! Авторизация не выполнена.");
      return;
    }

    const fetchSales = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/api/sales?storage_id=${warehouse.id}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();

        if (Array.isArray(data.data)) {
          setSales(data.data);
          localStorage.setItem("sales", JSON.stringify(data.data));
        } else {
          setSales([]);
        }
      } catch (err) {
        console.error("Ошибка загрузки продаж:", err);
        const saved = localStorage.getItem("sales");
        if (saved) setSales(JSON.parse(saved));
      } finally {
        setLoading(false);
      }
    };

    const fetchReturns = async () => {
      try {
        const res = await fetch(`${API_URL}/api/returns?storage_id=${warehouse.id}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();

        if (Array.isArray(data.data)) {
          setReturns(data.data);
          localStorage.setItem("returns", JSON.stringify(data.data));
        } else {
          setReturns([]);
        }
      } catch (err) {
        console.error("Ошибка загрузки возвратов:", err);
        const saved = localStorage.getItem("returns");
        if (saved) setReturns(JSON.parse(saved));
      }
    };

    fetchSales();
    fetchReturns();
  }, [warehouse]);

  return (
    <div className={styles.historyRoot}>
      {/* 🔹 Переключатели вкладок */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tabBtn} ${activeTab === "sales" ? styles.active : ""}`}
          onClick={() => setActiveTab("sales")}
        >
          Продажи
        </button>

        <button
          className={`${styles.tabBtn} ${activeTab === "returns" ? styles.active : ""}`}
          onClick={() => setActiveTab("returns")}
        >
          Возвраты
        </button>
      </div>

      {/* 🔹 Продажи */}
      {activeTab === "sales" && (
        <div className={styles.tabContent}>
          <div className={styles.headerRow}>
            <h2>Список продаж</h2>
            {loading && <span className={styles.loading}>Загрузка…</span>}
          </div>

          {/* Фильтры */}
          <div className={styles.filters}>
            <input placeholder="Номер документа" />
            <input placeholder="Сумма" />
            <input type="date" />
            <select>
              <option>Тип оплаты</option>
              <option>Наличные</option>
              <option>Карта</option>
            </select>
            <input placeholder="Фискальный номер" />
            <input placeholder="Клиент" />
            <input placeholder="Товар / Услуга" />
            <select>
              <option>Фискальный признак</option>
              <option>Есть</option>
              <option>Нет</option>
            </select>
            <select>
              <option>Возврат</option>
              <option>Да</option>
              <option>Нет</option>
            </select>
            <input placeholder="ИИН / БИН" />
          </div>

          {sales.length === 0 ? (
            <div className={styles.empty}>
              <p>Здесь пусто</p>
            </div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Номер документа</th>
                    <th>Дата</th>
                    <th>Сумма</th>
                    <th>Прибыль</th>
                    <th>Тип оплаты</th>
                    <th>Возврат</th>
                    <th>Статус</th>
                  </tr>
                </thead>

                <tbody>
                  {sales.map((row) => {
                    const payments = [];
                    if (row.cash && Number(row.cash) > 0) payments.push("Наличные");
                    if (row.cashless && Number(row.cashless) > 0) payments.push("Карта");

                    return (
                      <tr key={row.id}>
                        <td>{row.id}</td>
                        <td>{row.inserted_at ? new Date(row.inserted_at).toLocaleString() : "—"}</td>
                        <td>{row.total_sum ?? "—"} ₸</td>
                        <td>{row.profit ?? "—"} ₸</td>
                        <td>{payments.length ? payments.join(" / ") : "—"}</td>
                        <td>{row.returned ? "Да" : "Нет"}</td>
                        <td>{translateStatus(row.status)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* 🔹 Возвраты */}
      {activeTab === "returns" && (
        <div className={styles.tabContent}>
          <h2>Список возвратов</h2>

          {/* Фильтры */}
          <div className={styles.filters}>
            <input placeholder="Сумма" />
            <select>
              <option>Выберите период</option>
              <option>Сегодня</option>
              <option>Вчера</option>
              <option>Неделя</option>
            </select>
            <select>
              <option>Тип оплаты</option>
              <option>Наличные</option>
              <option>Карта</option>
            </select>
            <select>
              <option>Фискальный признак</option>
              <option>Есть</option>
              <option>Нет</option>
            </select>
            <input placeholder="Клиент" />
            <input placeholder="Товар / Услуга" />
          </div>

          {returns.length === 0 ? (
            <div className={styles.empty}>
              <p>Здесь пусто</p>
            </div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Номер чека</th>
                    <th>Дата</th>
                    <th>Сумма</th>
                    <th>Тип оплаты</th>
                  </tr>
                </thead>
                <tbody>
                  {returns.map((row, i) => (
                    <tr key={row?.id ?? i}>
                      <td>{row.checkNumber ?? "—"}</td>
                      <td>{row.date ?? "—"}</td>
                      <td>{row.amount ?? "—"} ₸</td>
                      <td>{row.payment ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HistoryBody;
